import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  IconButton,
  TextField,
  MenuItem,
  Chip,
} from '@mui/material';
import { Book, Add, Edit, Delete } from '@mui/icons-material';
import ImportExportBar from '../../components/ImportExport/ImportExportBar';
import { emitDataUpdated } from '../../utils/dataBus';
import { logAuditEvent } from '../../utils/audit';

interface Journal {
  id: string;
  code: string; // ACH, VTE, BQ, CAI, OD, DON, SUB
  libelle: string;
  type_journal: 'ACHATS' | 'VENTES' | 'BANQUE' | 'CAISSE' | 'OPERATIONS_DIVERSES' | 'DONS' | 'SUBVENTIONS';
  actif: boolean;
}

const STORAGE_KEY = 'comptaEBNL_journaux_local';
const DEFAULTS: Journal[] = [
  { id: 'ACH', code: 'ACH', libelle: 'Journal des Achats', type_journal: 'ACHATS', actif: true },
  { id: 'VTE', code: 'VTE', libelle: 'Journal des Ventes', type_journal: 'VENTES', actif: true },
  { id: 'BQ', code: 'BQ', libelle: 'Journal de Banque', type_journal: 'BANQUE', actif: true },
  { id: 'CAI', code: 'CAI', libelle: 'Journal de Caisse', type_journal: 'CAISSE', actif: true },
  { id: 'OD', code: 'OD', libelle: 'Opérations Diverses', type_journal: 'OPERATIONS_DIVERSES', actif: true },
  { id: 'DON', code: 'DON', libelle: 'Journal des Dons', type_journal: 'DONS', actif: true },
  { id: 'SUB', code: 'SUB', libelle: 'Journal des Subventions', type_journal: 'SUBVENTIONS', actif: true },
];

const Journaux: React.FC = () => {
  const [journaux, setJournaux] = useState<Journal[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Journal | null>(null);
  const [form, setForm] = useState<Journal>({ id: '', code: '', libelle: '', type_journal: 'ACHATS', actif: true });

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try { setJournaux(JSON.parse(raw)); } catch { setJournaux(DEFAULTS); }
    } else {
      setJournaux(DEFAULTS);
    }
  }, []);

  const saveAll = (items: Journal[]) => {
    setJournaux(items);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    emitDataUpdated();
    logAuditEvent({ module: 'journaux', action: 'save_all', details: { count: items.length } });
  };

  const handleNew = () => {
    setEditing(null);
    setForm({ id: '', code: '', libelle: '', type_journal: 'ACHATS', actif: true });
    setOpen(true);
  };

  const handleEdit = (j: Journal) => {
    setEditing(j);
    setForm({ ...j });
    setOpen(true);
  };

  const handleDelete = (id: string) => {
    saveAll(journaux.filter(j => j.id !== id));
    logAuditEvent({ module: 'journaux', action: 'delete', details: { id } });
  };

  const upsert = () => {
    const id = editing?.id || form.code || `${Date.now()}`;
    const payload: Journal = { ...form, id, code: form.code.toUpperCase() };
    const next = [...journaux];
    const idx = next.findIndex(j => j.id === id);
    if (idx >= 0) next[idx] = payload; else next.unshift(payload);
    saveAll(next);
    setOpen(false);
    logAuditEvent({ module: 'journaux', action: editing ? 'update' : 'create', details: { id } });
  };

  const parseCSV = (text: string): Record<string, string>[] => {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length === 0) return [];
    const headers = lines[0].split(',').map(h => h.trim());
    return lines.slice(1).map(line => {
      const cols = line.split(',');
      const row: Record<string, string> = {};
      headers.forEach((h, i) => { row[h] = (cols[i] || '').trim(); });
      return row;
    });
  };

  const onImportCSV = (csvText: string) => {
    // code,libelle,type_journal,actif
    const rows = parseCSV(csvText);
    const list: Journal[] = rows.map((r) => ({
      id: r.code?.toUpperCase() || `${Date.now()}`,
      code: r.code?.toUpperCase() || '',
      libelle: r.libelle || '',
      type_journal: (r.type_journal as Journal['type_journal']) || 'OPERATIONS_DIVERSES',
      actif: String(r.actif || 'true').toLowerCase() !== 'false',
    }));
    saveAll(list);
    logAuditEvent({ module: 'journaux', action: 'import_csv', details: { count: list.length } });
  };

  const onImportJSON = (jsonText: string) => {
    try {
      const data = JSON.parse(jsonText);
      if (Array.isArray(data)) saveAll(data as Journal[]);
      logAuditEvent({ module: 'journaux', action: 'import_json', details: { count: Array.isArray(data) ? data.length : 0 } });
    } catch {}
  };

  const getExportCSV = () => {
    const header = 'code,libelle,type_journal,actif';
    const lines = [header, ...journaux.map(j => [j.code, j.libelle.replace(/,/g,' '), j.type_journal, j.actif ? 'true' : 'false'].join(','))];
    return { filename: 'journaux.csv', content: lines.join('\n') };
  };

  const getExportJSON = () => ({ filename: 'journaux.json', content: JSON.stringify(journaux, null, 2) });

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Journaux Comptables
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Gestion des journaux (mode local)
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Book color="primary" />
              <Typography variant="h6">Liste des journaux</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <ImportExportBar onImportCSV={onImportCSV} onImportJSON={onImportJSON} getExportCSV={getExportCSV} getExportJSON={getExportJSON} />
              <Button variant="contained" startIcon={<Add />} onClick={handleNew}>
                Nouveau journal
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Libellé</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {journaux.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center">Aucun journal</TableCell></TableRow>
            ) : journaux.map(j => (
              <TableRow key={j.id} hover>
                <TableCell>{j.code}</TableCell>
                <TableCell>{j.libelle}</TableCell>
                <TableCell>{j.type_journal}</TableCell>
                <TableCell>
                  <Chip size="small" label={j.actif ? 'Actif' : 'Inactif'} color={j.actif ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleEdit(j)}><Edit fontSize="small"/></IconButton>
                  <IconButton size="small" onClick={() => handleDelete(j.id)}><Delete fontSize="small"/></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {open && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {editing ? 'Modifier un journal' : 'Nouveau journal'}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Code"
                  value={form.code}
                  onChange={e => setForm({ ...form, code: e.target.value })}
                  inputProps={{ maxLength: 4 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Libellé"
                  value={form.libelle}
                  onChange={e => setForm({ ...form, libelle: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  select
                  label="Type"
                  value={form.type_journal}
                  onChange={e => setForm({ ...form, type_journal: e.target.value as Journal['type_journal'] })}
                >
                  {['ACHATS','VENTES','BANQUE','CAISSE','OPERATIONS_DIVERSES','DONS','SUBVENTIONS'].map(t => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button onClick={() => setOpen(false)}>Annuler</Button>
              <Button variant="contained" onClick={upsert}>{editing ? 'Mettre à jour' : 'Enregistrer'}</Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Journaux;